import React, { useEffect, useState } from 'react';
import GoogleMapReact from 'google-map-react';
import { defaultCenterCoords } from 'core-lib/data/common';
import { LOCATION_STATUS_TYPES } from 'core-lib/utils/location';
import s from './Map.module.scss';
import { Theme } from 'core-lib/constants/setting';
import { debounce } from 'core-lib/utils/helpers';
import { gmapsTheme } from 'core-lib/constants/gmapsTheme';
import AnimatedImage from 'core-lib/components/AnimatedImage';
import { Typography } from 'ui-v2/components/Typography';

type MapProps = {
  onDragEnd: (any) => void;
  onMarkerClick?: (arg0: any, arg1: any, arg2: any) => void;
  markers?: any[];
  location: any;
  zoomLevel?: number;
  hideCurrentLocation?: boolean;
  activeMarker?: number;
  theme?: Theme;
};

const DEFAULT_ZOOM = 20;

const OPTIONS = {
  zoomControl: false,
  scaleControl: true,
  fullscreenControl: false,
  mapTypeControl: false,
  rotateControl: false,
  myLocationControl: true,
  clickableIcons: false,
  styles: gmapsTheme,
  maxZoom: 18,
  minZoom: 10,
  gestureHandling: 'greedy' 
};

function parseCoord(value: unknown) {
  const parsed = parseFloat(String(value));
  return Number.isNaN(parsed) ? null : parsed;
}

function getCenter(loc) {
  if (
    loc?.activeLocType === 'device' &&
    loc?.deviceLoc?.status === LOCATION_STATUS_TYPES.Success
  ) {
    const lat = parseCoord(loc?.deviceLoc?.coords?.lat);
    const lng = parseCoord(loc?.deviceLoc?.coords?.lng);

    if (lat != null && lng != null) {
      return { lat, lng };
    }
  }

  const searchLat = parseCoord(loc?.searchLoc?.coords?.lat);
  const searchLng = parseCoord(loc?.searchLoc?.coords?.lng);

  if (searchLat != null && searchLng != null) {
    return { lat: searchLat, lng: searchLng };
  }

  // Only reuse device coords when geolocation actually succeeded.
  if (loc?.deviceLoc?.status === LOCATION_STATUS_TYPES.Success) {
    const deviceLat = parseCoord(loc?.deviceLoc?.coords?.lat);
    const deviceLng = parseCoord(loc?.deviceLoc?.coords?.lng);

    if (deviceLat != null && deviceLng != null) {
      return { lat: deviceLat, lng: deviceLng };
    }
  }

  return null;
}

export function Map({
  onDragEnd,
  markers,
  zoomLevel,
  hideCurrentLocation,
  onMarkerClick,
  location,
  activeMarker,
  theme,
}: MapProps) {
  const [mapApi, setMapApi] = React.useState<any>({
    loaded: false,
    map: null,
    maps: null,
  });
  const [zoom, setZoom] = React.useState<number>(zoomLevel || DEFAULT_ZOOM);
  const [center, setCenter] = useState(null);
  const [showMap, setShowMap] = useState(true);

  const [showInfo, setShowInfo] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setShowInfo(false);
    }, 3000);
  }, []);

  useEffect(() => {
    const temp = getCenter(location);
    if (temp) {
      setCenter(temp);
    }
  }, [location]);

  useEffect(() => {
    setShowMap(false);
    setTimeout(() => {
      setShowMap(true);
    });
  }, [theme]);

  return (
    <div className={s.root}>
      <div className={s.markerPosition}>
        {showInfo && (
          <div className={s.info}>
            <Typography
              label={"Move the map to adjust your location"}
              variant="tiny_medium"
              //color="on_dark_bg"
              style={{ textAlign: 'center' }}
            />
          </div>
        )}
        <AnimatedImage src="/app/img/icons/parking-pin-blue.svg" alt="Marker" />
      </div>
      <GoogleMapReact
        defaultCenter={defaultCenterCoords}
        center={center}
        defaultZoom={DEFAULT_ZOOM}
        zoom={zoom}
        options={{
          ...OPTIONS,
          styles: gmapsTheme,
        }}
        onZoomAnimationEnd={(zoom) => setZoom(zoom)}
        onGoogleApiLoaded={({ map, maps }) => {
          setMapApi({
            loaded: true,
            map,
            maps,
          });
        }}
        yesIWantToUseGoogleMapApiInternals
        onDragEnd={(map) => {
          debounce(async () => {
            const lat = map.center.lat();
            const lng = map.center.lng();
            await onDragEnd({ lat, lng });
          }, 800);
        }}
      />
    </div>
  );
}
